import { useEffect, useRef } from 'react';

import { RootState } from 'store/store';
import { useAppSelector, useAppStore } from 'store/store-hooks';
import { isNull } from 'components/app/_helpers/_app.helper';
import { getRangeMarkerTimes } from 'components/app/_mediator/range-markers/range-markers.helper';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { useSbPlayerContext } from './sb-player.provider';
import { EnumFader, useSbPlayerCb } from './use-sb-player-cb';

type Payload = {
  start: Milliseconds;
  end: Milliseconds;
  fadeIn: Milliseconds;
  fadeOut: Milliseconds;
};

const VAR__TRANSITION = '--fader-transition-time';
const VAR__OPACITY = '--fader-opacity';
const VAR__DELAY = '--fader-delay';
const VAR__TEXT = 'data-text';
const CLASS__FADE_OUT = 'fader--out';

export const useSbFader = () => {
  const { toggleFader } = useSbPlayerCb();
  const { bgPayload, textPayload } = usePayload();
  const { faderText, faderBg } = useSbPlayerContext();
  const handleTextFader = useElementFader(faderText, textPayload);
  const handleBgFader = useElementFader(faderBg, bgPayload);
  const store = useAppStore();

  const handleFaderToggle = () => {
    const { cursorCell, storyConfig } = selectData(store.getState());
    if (!storyConfig) return;
    const { startMarkerId, finishMarkerId } = storyConfig;
    if (
      (!isNull(startMarkerId) && cursorCell <= startMarkerId) ||
      (!isNull(finishMarkerId) && cursorCell > finishMarkerId)
    )
      toggleFader(EnumFader.off);
    else toggleFader(EnumFader.on);
  };

  const handleFader = (cursorTime: Milliseconds) => {
    const time = cursorTime * 1000;
    handleTextFader(time);
    handleBgFader(time);
  };

  return { handleFader, handleFaderToggle };
};

/* H O O K S */

const useElementFader = (
  element: React.MutableRefObject<HTMLDivElement | null>,
  payload: React.MutableRefObject<Payload | undefined>
) => {
  const { faderText } = useSbPlayerContext();
  const { wsGetCursorTime } = useAudioTrackContext();
  const tracksRestored = useAppSelector((state) => state.memory.tracksRestored);
  const config = useAppSelector((state) => state.editor.storyConfig);
  const isFaderOut = useRef<boolean>();
  const store = useAppStore();

  const setOpacity = (opacity: Fraction) => {
    element.current?.style.setProperty(VAR__OPACITY, opacity + '');
  };

  const setTransition = (transition: Milliseconds, delay: Milliseconds) => {
    element.current?.style.setProperty(VAR__TRANSITION, transition + 'ms');
    element.current?.style.setProperty(VAR__DELAY, delay + 'ms');
  };

  const setFaderOut = (isOut: boolean) => {
    if (isFaderOut.current === isOut) return;
    if (isOut) element.current?.classList.add(CLASS__FADE_OUT);
    else element.current?.classList.remove(CLASS__FADE_OUT);
    isFaderOut.current = isOut;
    handleText();
  };

  const handleText = () => {
    const { storyConfig } = selectData(store.getState());
    if (element.current !== faderText.current || !storyConfig) return;
    const { creditsText, titleText } = storyConfig;
    faderText.current!.setAttribute(VAR__TEXT, `${isFaderOut.current ? creditsText : titleText}`);
  };

  useEffect(() => {
    const onTransitionEnd = () => {
      if (isFaderOut.current) return;
      setFaderOut(true);
      const { end, fadeOut } = payload.current!;
      const idle = end - wsGetCursorTime('ms') - fadeOut;
      setTransition(fadeOut, idle);
      setOpacity(0);
    };
    if (!tracksRestored) return;
    const ref = element.current!;
    ref.addEventListener('transitionend', onTransitionEnd);
    return () => ref.removeEventListener('transitionend', onTransitionEnd);
  }, [tracksRestored]);

  useEffect(() => {
    handleText();
  }, [config?.creditsText, config?.titleText]);

  return (time: Milliseconds) => {
    const { start, end, fadeOut, fadeIn } = payload.current!;
    const position = time <= fadeIn + start ? 'start' : time < end - fadeOut ? 'middle' : 'end';
    setFaderOut(position !== 'start');
    if (position === 'start') {
      if (time < start) {
        setTransition(fadeIn, start - time);
        setOpacity(1);
        return;
      }
      const transition = start + fadeIn - time;
      setTransition(transition, 0);
      setOpacity(transition / fadeIn);
    } else if (position === 'middle') {
      setTransition(fadeOut, end - fadeOut - time);
      setOpacity(0);
    } else {
      if (time >= end) {
        setTransition(0, 0);
        setOpacity(1);
        return;
      }
      const transition = end - time;
      setTransition(transition, 0);
      setOpacity(1 - transition / fadeOut);
    }
  };
};

const usePayload = () => {
  const duration = useAppSelector((state) => state.audio.duration);
  const markers = useAppSelector((state) => state.editor.markers);
  const config = useAppSelector((state) => state.editor.storyConfig);
  const textPayload = useRef<Payload>();
  const bgPayload = useRef<Payload>();

  useEffect(() => {
    if (!config || !markers || !duration) return;
    const { startMarkerId, finishMarkerId, titleDuration, creditsDuration, trackFadeOutTime, trackFadeInTime } = config;

    const { startTime, endTime } = getRangeMarkerTimes({
      startMarkerId,
      finishMarkerId,
      type: 'ms',
      duration,
      markers,
    });

    bgPayload.current = {
      start: startTime,
      end: endTime,
      fadeIn: trackFadeInTime,
      fadeOut: trackFadeOutTime,
    };
    textPayload.current = { start: startTime, end: endTime, fadeIn: titleDuration, fadeOut: creditsDuration };
  }, [config, duration, markers]);

  return { bgPayload, textPayload };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { storyConfig } = state.editor;
  const cursorCell = state.editor__controls.sbPosition.cell;
  return { cursorCell, storyConfig };
};
